import * as React from "react";
import { BillingAccount, BillingInvoice, BillingUserRole } from "../../schema/types/account";
import { getLocalToken, request } from "../../services/cpln";
import { sortBy } from "lodash";
import { getBillingErrorMessage } from "../../services/utils";
import { Alert, Button, notification } from "antd";
import { Table } from "../../newcomponents/table/table";
import { DateString } from "../../components/dateString";
import { formatStrings } from "../../utils/time";
import { DateRangePicker, RangePreset } from "../../newcomponents/datepicker";
import { IS_DEPLOYMENT_ENV_TEST } from "../../envVariables";

// Helper functions for UTC date operations
function startOfMonthUTC(date: Date): Date {
  const d = new Date(date);
  d.setUTCDate(1);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

function startOfYearUTC(date: Date): Date {
  const d = new Date(date);
  d.setUTCMonth(0, 1);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

function addMonthsUTC(date: Date, months: number): Date {
  const d = new Date(date);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d;
}

function addYearsUTC(date: Date, years: number): Date {
  const d = new Date(date);
  d.setUTCFullYear(d.getUTCFullYear() + years);
  return d;
}

// Invoice presets - monthly billing cycles
function getInvoicePresets(): RangePreset[] {
  const now = new Date();
  const thisMonthStart = startOfMonthUTC(now);
  const thisYearStart = startOfYearUTC(now);

  return [
    {
      label: "Last Month",
      value: [addMonthsUTC(thisMonthStart, -1).toISOString(), thisMonthStart.toISOString()],
    },
    {
      label: "Last 3 Months",
      value: [addMonthsUTC(thisMonthStart, -3).toISOString(), addMonthsUTC(thisMonthStart, 1).toISOString()],
    },
    {
      label: "Last 6 Months",
      value: [addMonthsUTC(thisMonthStart, -6).toISOString(), addMonthsUTC(thisMonthStart, 1).toISOString()],
    },
    {
      label: "Last 12 Months",
      value: [addMonthsUTC(thisMonthStart, -12).toISOString(), addMonthsUTC(thisMonthStart, 1).toISOString()],
    },
    {
      label: "This Year",
      value: [thisYearStart.toISOString(), addYearsUTC(thisYearStart, 1).toISOString()],
    },
    {
      label: "Last Year",
      value: [addYearsUTC(thisYearStart, -1).toISOString(), thisYearStart.toISOString()],
    },
  ];
}

interface Props {
  account: BillingAccount;
  roles: BillingUserRole[];
}
export const Invoices: React.FC<Props> = ({ account, roles }) => {
  const [invoices, setInvoices] = React.useState<BillingInvoice[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string>("");

  // Initialize with "Last 3 Months" range by default
  const [startISO, setStartISO] = React.useState<string | null>(() => {
    const now = new Date();
    return addMonthsUTC(startOfMonthUTC(now), -3).toISOString();
  });
  const [endISO, setEndISO] = React.useState<string | null>(() => {
    const now = new Date();
    return addMonthsUTC(startOfMonthUTC(now), 1).toISOString();
  });

  const invoicePresets = React.useMemo(() => getInvoicePresets(), []);

  React.useEffect(() => {
    if (startISO && endISO) {
      fetchInvoices(startISO, endISO);
    }
  }, [startISO, endISO]);

  async function fetchInvoices(start: string, end: string) {
    try {
      setIsLoading(true);
      setInvoices([]);
      setError("");

      const { data } = await request({
        service: "billing-ng",
        url: `/account/${account.id}/charges/invoices?start=${start}&end=${end}`,
      });
      setInvoices(sortBy(data.invoices, (i) => i.startTime).reverse());
      setIsLoading(false);
    } catch (e) {
      setError(getBillingErrorMessage(e));
      setIsLoading(false);
    }
  }

  async function viewInvoicePdf(invoiceId: string) {
    try {
      setIsLoading(true);

      const { accessToken } = await getLocalToken();

      const headers = new Headers();
      headers.append("Content-Type", "application/pdf");
      headers.append("Accept", "application/pdf");
      headers.append("Authorization", `Bearer ${accessToken}`);

      const requestOptions: any = {
        method: "GET",
        headers,
      };

      const serviceUrl = `https://billing-ng${IS_DEPLOYMENT_ENV_TEST ? ".test" : ""}.cpln.io`;

      const response = await fetch(`${serviceUrl}/account/${account.id}/charges/invoices/${invoiceId}`, requestOptions);

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const newTab = window.open();
      if (newTab) {
        newTab.location.href = url;
      } else {
        notification.warning({
          message: "Failed to open the invoice pdf",
        });
      }

      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      notification.warning({
        message: "Failed to open the invoice pdf",
        description: getBillingErrorMessage(e),
      });
    }
  }

  if (error) {
    return (
      <div>
        <Alert className="mb-4" type={"error"} message={error} />
        <Button
          htmlType={`button`}
          type="primary"
          onClick={async () => {
            if (startISO && endISO) {
              await fetchInvoices(startISO, endISO);
            }
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      <Table<BillingInvoice>
        title={() => {
          return (
            <div className="flex items-center gap-4">
              <div className="font-semibold">Invoices</div>
              <DateRangePicker
                from={startISO}
                to={endISO}
                setFrom={setStartISO}
                setTo={setEndISO}
                granularity="date"
                presets={invoicePresets}
                immediatelyResolveExpressions
                tooltipPosition="bottom"
              />
            </div>
          );
        }}
        data={invoices}
        tableId={"account-invoices-update4"}
        enableFilter={false}
        isLoading={isLoading}
        hideSettings
        columns={[
          {
            id: "startTime",
            label: "Start Time",
            enableResize: true,
            size: 300,
            cell: ({ row }) => {
              const invoice = row.original;
              return (
                <div>
                  <DateString iso={invoice.startTime} format={formatStrings.log} />
                </div>
              );
            },
          },
          {
            id: "endTime",
            label: "End Time",
            enableResize: true,
            size: 300,
            cell: ({ row }) => {
              const invoice = row.original;
              return (
                <div>
                  <DateString iso={invoice.endTime} format={formatStrings.log} />
                </div>
              );
            },
          },
          {
            id: "totalCharge",
            label: "Total Charge",
            size: 140,
            cell: ({ getValue, row }) => {
              const invoice = row.original;
              const value = getValue() / 100;
              const currency = invoice.periods[0].accountState.ratePlan.currency;
              return (
                <div>
                  {currency === "USD" ? "$" : ""}
                  {`${value} ${currency === "usd" ? "" : currency}`}
                </div>
              );
            },
          },
          {
            id: "pdf",
            label: "PDF",
            size: 160,
            cell: (p) => {
              return (
                <Button
                  htmlType={`button`}
                  type="default"
                  onClick={() => viewInvoicePdf(p.row.original.id)}
                  loading={isLoading}
                  disabled={isLoading}
                >
                  View Invoice
                </Button>
              );
            },
          },
        ]}
      />
    </>
  );
};
